import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ConnectedApp, ConnectedAppAuth, ConnectedAppEndpoint, Schemes } from 'app/models/connected-app';
import { ExternalOperationAuthMethod } from 'app/models/external-operation-common-settings';
import { ConnectedAppService } from 'app/services/connected-app.service';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-add-edit-external-operation-common-settings',
  templateUrl: './add-edit-external-operation-common-settings.component.html',
  styleUrls: ['./add-edit-external-operation-common-settings.component.scss']
})
export class AddEditExternalOperationCommonSettingsComponent implements OnInit {

  connectedApps: ConnectedApp[] = [];
  currentAppEndpoints: ConnectedAppEndpoint[] = [];
  currentEndpointAuthOptions: ExternalOperationAuthMethod[][] = [];

  isLoadingAppsList: boolean = false;
  isLoadingEndpointsList: boolean = false;
  isLoadingCurrentEndpoint: boolean = false;

  private _formGroup: FormGroup = null;

  public get isLoading() : boolean {
    return this.isLoadingAppsList || this.isLoadingEndpointsList || this.isLoadingCurrentEndpoint;
  }
  
  @Output() endpointChanged: EventEmitter<{connectedAppEndpoint: ConnectedAppEndpoint, changedByUser: boolean}> = new EventEmitter();

  @Input() public set formGroup(v : FormGroup) {
    this._formGroup = v;
    this.isLoadingAppsList = true;
    this.connectedAppService.list().pipe(finalize(() => this.isLoadingAppsList = false)).subscribe((c) => {
      this.connectedApps = c;
      this.loadEndpointsForCurrentApp();
      this.loadCurrentEndpoint();
    });
  }
  
  public get formGroup() : FormGroup {
    return this._formGroup;
  }

  constructor(private connectedAppService: ConnectedAppService) {
  }

  ngOnInit(): void {
  }

  loadCurrentEndpoint(changedByUser: boolean = false) {
    if (changedByUser) {
      this._formGroup.get('connected_app_endpoint').setValue(null);
      this._formGroup.get('scheme').setValue(null);
      this._formGroup.get('accepts').setValue(null);
      this._formGroup.get('returns').setValue(null);
      this._formGroup.get('auth_methods').setValue(null);
    }
    this.currentEndpointAuthOptions = [];

    if (this._formGroup.get('connected_app_endpoint_id') && this._formGroup.get('connected_app_endpoint_id').value) {
      this.isLoadingCurrentEndpoint = true;
      this.connectedAppService.getEndpointById(this._formGroup.get('connected_app_id').value, this._formGroup.get('connected_app_endpoint_id').value).pipe(finalize(() => this.isLoadingCurrentEndpoint = false)).subscribe((e) => {
        e.schemes = e.schemes || [Schemes.HTTPS];

        if (changedByUser) {
          e.schemes && e.schemes[0] && this._formGroup.get('scheme').setValue(e.schemes[0]);
          e.accepts && e.accepts[0] && this._formGroup.get('accepts').setValue(e.accepts[0]);
          e.returns && e.returns[0] && this._formGroup.get('returns').setValue(e.returns[0]);
        }

        this._formGroup.get('connected_app_endpoint').setValue(e);

        // Build auth options
        (e.auth_methods || []).forEach((authGroup) => {
          let extOpAuthGroup: ExternalOperationAuthMethod[] = [];
          authGroup.forEach((a) => {
            let connectedAppAuth: ConnectedAppAuth = this.connectedApps.find((c) => c.id == this._formGroup.get('connected_app_id').value).auths.find((globalAuth) => globalAuth.name == a.auth_name);
            connectedAppAuth = (<ConnectedAppAuth>JSON.parse(JSON.stringify(connectedAppAuth)));
            connectedAppAuth.oauth2_scopes = (connectedAppAuth.oauth2_scopes || []).filter((scope) => (a.scopes || []).includes(scope.name));
            extOpAuthGroup.push({
              connected_app_auth_id: connectedAppAuth.id,
              connected_app_auth:  connectedAppAuth,
              oauth2_scopes: a.scopes
            });
          });
          this.currentEndpointAuthOptions.push(extOpAuthGroup);
        });

        let selectedAuthMethods: ExternalOperationAuthMethod[] = this._formGroup.get('auth_methods').value;
        if (this.currentEndpointAuthOptions.length > 0) {
          if (changedByUser) {
            this._formGroup.get('auth_methods').setValue(this.currentEndpointAuthOptions[0]);
          } else if (selectedAuthMethods) {
            let selectedAuthOption = this.currentEndpointAuthOptions.find((authArray) => {
                let isExactMatch: boolean = false;
                if (authArray.length == selectedAuthMethods.length) {
                  authArray.forEach((authMethod, index) => {
                    isExactMatch = isExactMatch && (authMethod.connected_app_auth_id == selectedAuthMethods[index].connected_app_auth_id &&
                                                  (authMethod.oauth2_scopes || []).join(',') == (selectedAuthMethods[index].oauth2_scopes || []).join(','));
                  });
                }

                return isExactMatch;
              });
            if (selectedAuthOption) {
              this._formGroup.get('auth_methods').setValue(selectedAuthOption);
            } else {
              this._formGroup.get('auth_methods').setValue(this.currentEndpointAuthOptions[0]);
            }
          } else {
            this._formGroup.get('auth_methods').setValue(this.currentEndpointAuthOptions[0]);
          }
        } else {
          this._formGroup.get('auth_methods').setValue([]);
        }

        this.endpointChanged.emit({connectedAppEndpoint: e, changedByUser: changedByUser});

      });
    } else {
      this.endpointChanged.emit({connectedAppEndpoint: null, changedByUser: changedByUser});
    }
  }

  loadEndpointsForCurrentApp(changedByUser: boolean = false) {
    this.currentAppEndpoints = [];
    if (changedByUser) {
      this._formGroup.get('connected_app_endpoint_id').setValue(null);
    }

    if (this._formGroup.get('connected_app_id') && this._formGroup.get('connected_app_id').value) {
      this.isLoadingEndpointsList = true;
      this.connectedAppService.listEndpoints(this._formGroup.get('connected_app_id').value).pipe(finalize(() => this.isLoadingEndpointsList = false)).subscribe((e) => {
        this.currentAppEndpoints = e;
      });
    } else {
      this.endpointChanged.emit({connectedAppEndpoint: null, changedByUser: changedByUser});
    }
  }
}
